import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const CartContext = createContext(null);

const STORAGE_KEY = "wc.cart.v1";
const MAX_QTY_DEFAULT = 99;

/** Valide et normalise un item */
function normalizeItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "").trim();
  if (!id) return null;
  const name = String(raw.name || "").trim();
  const price = Number(raw.price);
  const safePrice = Number.isFinite(price) && price >= 0 ? price : 0;
  const qty = Math.max(0, Math.floor(Number(raw.qty) || 0));
  const imageUrl = raw.imageUrl || null;
  // facultatif : variantKey pour différencier deux variantes avec même id
  const variantKey = raw.variantKey ? String(raw.variantKey) : undefined;
  return { id, name, price: safePrice, qty, imageUrl, ...(variantKey ? { variantKey } : {}) };
}

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    const normalized = arr.map(normalizeItem).filter(Boolean).filter((it) => it.qty > 0);
    return normalized;
  } catch {
    return [];
  }
}

function saveCart(cart) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  } catch {}
}

/** Merge key = id + variantKey (si présent) */
function sameLine(a, b) {
  return a.id === b.id && (a.variantKey || "") === (b.variantKey || "");
}

export function CartProvider({ children, maxQty = MAX_QTY_DEFAULT }) {
  const [cart, setCart] = useState(() => loadCart());

  // Persistance
  useEffect(() => saveCart(cart), [cart]);

  // Sync multi-onglets
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setCart(loadCart());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /** Ajoute ou met à jour une ligne
   * mode: 'add' (par défaut, incrémente) ou 'set' (remplace la quantité)
   */
  const addToCart = (item, qty = 1, { mode = "add" } = {}) => {
    const norm = normalizeItem({ ...item, qty });
    if (!norm) return;
    const q = Math.max(1, Math.min(maxQty, Math.floor(qty || 1)));
    setCart((prev) => {
      const idx = prev.findIndex((p) => sameLine(p, norm));
      if (idx >= 0) {
        const next = [...prev];
        const current = next[idx].qty || 0;
        next[idx] = {
          ...next[idx],
          name: norm.name || next[idx].name,
          price: Number.isFinite(norm.price) ? norm.price : next[idx].price,
          imageUrl: norm.imageUrl ?? next[idx].imageUrl,
          qty: mode === "set" ? q : Math.min(maxQty, current + q),
        };
        return next;
      }
      return [...prev, { ...norm, qty: q }];
    });
  };

  const removeFromCart = (id, variantKey = "") => {
    setCart((prev) => prev.filter((i) => !(i.id === id && (i.variantKey || "") === variantKey)));
  };

  const updateQty = (id, qty, variantKey = "") => {
    const q = Math.max(0, Math.min(maxQty, Math.floor(Number(qty) || 0)));
    setCart((prev) => {
      if (q === 0) return prev.filter((i) => !(i.id === id && (i.variantKey || "") === variantKey));
      return prev.map((i) =>
        i.id === id && (i.variantKey || "") === variantKey ? { ...i, qty: q } : i
      );
    });
  };

  const increment = (id, step = 1, variantKey = "") => {
    setCart((prev) =>
      prev.map((i) =>
        i.id === id && (i.variantKey || "") === variantKey
          ? { ...i, qty: Math.min(maxQty, (i.qty || 0) + Math.max(1, step)) }
          : i
      )
    );
  };

  const decrement = (id, step = 1, variantKey = "") => {
    setCart((prev) =>
      prev
        .map((i) =>
          i.id === id && (i.variantKey || "") === variantKey
            ? { ...i, qty: Math.max(0, (i.qty || 0) - Math.max(1, step)) }
            : i
        )
        .filter((i) => i.qty > 0)
    );
  };

  const clearCart = () => setCart([]);

  const getItem = (id, variantKey = "") =>
    cart.find((i) => i.id === id && (i.variantKey || "") === variantKey) || null;

  // Sélecteurs mémoïsés
  const { itemCount, distinctCount, total } = useMemo(() => {
    const itemCount = cart.reduce((s, i) => s + (i.qty || 0), 0);
    const distinctCount = cart.length;
    const total = cart.reduce((s, i) => s + (Number(i.price) || 0) * (i.qty || 0), 0);
    return { itemCount, distinctCount, total };
  }, [cart]);

  // Utils pratiques
  const formatXOF = useMemo(
    () => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF" }),
    []
  );

  const openWhatsAppCart = (phone /* ex: "393895513136" */) => {
    const lines = cart
      .map(
        (i) =>
          `• ${i.name}${i.variantKey ? " (" + i.variantKey + ")" : ""} × ${i.qty} = ${formatXOF.format(
            (Number(i.price) || 0) * (i.qty || 0)
          )}`
      )
      .join("\n");
    const msg = encodeURIComponent(
      `Bonjour 👋, voici mon panier:\n${lines}\n\nTotal: ${formatXOF.format(total)}`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQty,
    increment,
    decrement,
    clearCart,
    getItem,
    itemCount,
    distinctCount,
    total,
    maxQty,
    formatXOF,
    openWhatsAppCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/** Hook de sécurité si utilisé hors Provider */
export function useCartContext() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
